import { useState, useEffect } from 'react';
import { Mail, ArrowLeft, PlayCircle, Loader2, CheckCircle2, Save, LayoutTemplate, Edit, X, Plus, Trash2, Palette, Inbox, RefreshCw, Send, ShieldCheck, AlertCircle, Sparkles, Check, Globe, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSettings, DbEmailSetting, DbEmailTemplate } from '@/lib/useSettings';
import { supabase } from '@/lib/supabase';
import { configurationsApi } from '@/api/configurationsApi';
import { settingsApi } from '@/api/settingsApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export function EmailConfigurationPage({ 
  onBack, 
  initialTab = 'smtp',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { emailSettings, saveEmailSettings, loading, mailboxes, saveMailbox, deleteMailbox, emailTemplates, saveEmailTemplate } = useSettings();
  
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [testErrorMessage, setTestErrorMessage] = useState<string | null>(null);

  const [testEmail, setTestEmail] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);

  const [formData, setFormData] = useState<Partial<DbEmailSetting>>(emailSettings || {});

  // Email-to-Ticket state
  const [isSyncingMailbox, setIsSyncingMailbox] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);

  const [e2tEnabled, setE2tEnabled] = useState(true);
  const [unknownSenderPolicy, setUnknownSenderPolicy] = useState<'create' | 'reject'>('create');
  const [defaultPriority, setDefaultPriority] = useState('Medium');
  const [defaultCategory, setDefaultCategory] = useState('Hardware');
  const [autoAssign, setAutoAssign] = useState(true);
  const [e2tSaveNotice, setE2tSaveNotice] = useState(false);

  // Ticket Portal & Redirection state
  const [portalUrl, setPortalUrl] = useState('https://sdtest.pinochle.ai');
  const [detailPath, setDetailPath] = useState('/tickets/{ticket_number}');
  const [isSavingPortal, setIsSavingPortal] = useState(false);
  const [portalSaveNotice, setPortalSaveNotice] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [portalAudit, setPortalAudit] = useState<{ updatedAt?: string; updatedByName?: string } | null>(null);

  useEffect(() => {
    async function loadPortalConfig() {
      try {
        const res = await settingsApi.getTicketPortalConfig();
        if (res.success && res.data) {
          if (res.data.ticketPortalUrl) setPortalUrl(res.data.ticketPortalUrl);
          if (res.data.ticketDetailPath) setDetailPath(res.data.ticketDetailPath);
          setPortalAudit({
            updatedAt: res.data.updatedAt,
            updatedByName: res.data.updatedByName
          });
        }
      } catch (e) {
        // Fallback default
      }
    }
    loadPortalConfig();
  }, []);

  useEffect(() => {
    const loadE2tConfig = async () => {
      try {
        const data = await configurationsApi.getConfiguration('email_to_ticket_settings');
        if (data && data.value) {
          if (typeof data.value.e2tEnabled === 'boolean') setE2tEnabled(data.value.e2tEnabled);
          if (data.value.unknownSenderPolicy) setUnknownSenderPolicy(data.value.unknownSenderPolicy);
          if (data.value.defaultPriority) setDefaultPriority(data.value.defaultPriority);
          if (data.value.defaultCategory) setDefaultCategory(data.value.defaultCategory);
          if (typeof data.value.autoAssign === 'boolean') setAutoAssign(data.value.autoAssign);
          return;
        }
      } catch (e) {
        // Fallback
      }

      if (!supabase) return;
      try {
        const { data } = await supabase
          .from('configurations')
          .select('*')
          .eq('key', 'email_to_ticket_settings')
          .maybeSingle();

        if (data && data.value) {
          if (typeof data.value.e2tEnabled === 'boolean') setE2tEnabled(data.value.e2tEnabled);
          if (data.value.unknownSenderPolicy) setUnknownSenderPolicy(data.value.unknownSenderPolicy);
          if (data.value.defaultPriority) setDefaultPriority(data.value.defaultPriority);
          if (data.value.defaultCategory) setDefaultCategory(data.value.defaultCategory);
          if (typeof data.value.autoAssign === 'boolean') setAutoAssign(data.value.autoAssign);
        }
      } catch (e) {
        console.error('Failed to load E2T config:', e);
      }
    };
    loadE2tConfig();
  }, []);

  // Inbound Email Simulator state
  const [simSenderEmail, setSimSenderEmail] = useState('rvasudevan@pinochle.ai');
  const [simSenderName, setSimSenderName] = useState('Raja Vasudevan');
  const [simSubject, setSimSubject] = useState('Laptop is not working');
  const [simBody, setSimBody] = useState('Hi IT Team,\n\nMy laptop is not starting after restarting.\nPlease help me resolve this issue.\n\nThanks,\nRaja');
  const [isSimulating, setIsSimulating] = useState(false);
  const [simResult, setSimResult] = useState<any>(null);

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Partial<DbEmailTemplate> | null>(null);

  const [isMailboxModalOpen, setIsMailboxModalOpen] = useState(false);
  const [editingMailbox, setEditingMailbox] = useState<any>(null);

  const handleSyncMailbox = async () => {
    setIsSyncingMailbox(true);
    setSyncResult(null);
    try {
      const res = await fetch('/api/email/sync', { method: 'POST' });
      const data = await res.json();
      setSyncResult(data);
    } catch (e: any) {
      setSyncResult({ success: false, error: e.message });
    } finally {
      setIsSyncingMailbox(false);
    }
  };

  const handleSimulateEmail = async () => {
    if (!simSenderEmail || !simSubject) return;
    setIsSimulating(true);
    setSimResult(null);
    try {
      const res = await fetch('/api/email/test-ingest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senderEmail: simSenderEmail,
          senderName: simSenderName,
          subject: simSubject,
          body: simBody,
          unknownSenderPolicy
        })
      });
      const data = await res.json();
      setSimResult(data);
      try {
        localStorage.setItem('compass_tickets_sync', Date.now().toString());
      } catch (e) {}
      window.dispatchEvent(new Event('compass_tickets_changed'));
    } catch (e: any) {
      setSimResult({ success: false, error: e.message });
    } finally {
      setIsSimulating(false);
    }
  };

  const handleSaveE2TSettings = async () => {
    try {
      const settingsPayload = {
        e2tEnabled,
        unknownSenderPolicy,
        defaultPriority,
        defaultCategory,
        autoAssign
      };

      await configurationsApi.saveConfiguration('email_to_ticket_settings', settingsPayload);
      setE2tSaveNotice(true);
      setTimeout(() => setE2tSaveNotice(false), 3000);
    } catch (err) {
      console.error('Failed to persist E2T settings:', err);
    }
  };

  const handleSavePortal = async () => {
    setIsSavingPortal(true);
    setPortalSaveNotice(null);
    setPortalError(null);

    const trimmed = portalUrl.trim();
    if (!trimmed.startsWith('https://') && !trimmed.startsWith('http://localhost') && !trimmed.startsWith('http://127.0.0.1')) {
      setPortalError('Ticket Portal URL must begin with https:// (e.g. https://sdtest.pinochle.ai).');
      setIsSavingPortal(false);
      return;
    }

    try {
      const res = await settingsApi.updateTicketPortalConfig({
        ticketPortalUrl: trimmed,
        ticketDetailPath: detailPath.trim()
      });
      if (res.success && res.data) {
        setPortalUrl(res.data.ticketPortalUrl);
        setDetailPath(res.data.ticketDetailPath);
        setPortalAudit({
          updatedAt: res.data.updatedAt,
          updatedByName: res.data.updatedByName
        });
        setPortalSaveNotice('Ticket Portal configuration saved and audit logged successfully.');
        setTimeout(() => setPortalSaveNotice(null), 4000);
      } else {
        setPortalError(res.error || 'Failed to save ticket portal configuration.');
      }
    } catch (e: any) {
      setPortalError(e.response?.data?.error || e.message || 'Failed to save configuration.');
    } finally {
      setIsSavingPortal(false);
    }
  };

  const handleTestLink = async () => {
    setPortalError(null);
    try {
      const res = await settingsApi.testTicketPortalUrl({
        ticketPortalUrl: portalUrl.trim(),
        ticketDetailPath: detailPath.trim(),
        sampleTicketNumber: 'INC-9389'
      });
      if (res.success && res.testUrl) {
        window.open(res.testUrl, '_blank');
      } else {
        setPortalError(res.error || 'Invalid test link URL.');
      }
    } catch (e: any) {
      setPortalError(e.response?.data?.error || e.message || 'Failed to generate test link.');
    }
  };

  const handleEditTemplate = (template: Partial<DbEmailTemplate> = { name: '', subject_template: '', html_body: '', text_body: '', language: 'en', is_active: true }) => {
    setEditingTemplate({ ...template });
    setIsTemplateModalOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    await saveEmailTemplate(editingTemplate);
    setIsTemplateModalOpen(false);
  };

  const handleEditMailbox = (mailbox: any = { email: '', name: '', department: '' }) => {
    setEditingMailbox({ ...mailbox });
    setIsMailboxModalOpen(true);
  };

  const handleSaveMailbox = async () => {
    if (!editingMailbox) return;
    await saveMailbox(editingMailbox);
    setIsMailboxModalOpen(false);
  };

  useEffect(() => {
    if (emailSettings) {
      setFormData(emailSettings);
    }
  }, [emailSettings]);

  const handleTestConnection = async () => {
    if (!formData.client_id || !formData.tenant_id || !formData.client_secret || !formData.from_email || !testEmail) {
      setTestResult('error');
      setTestErrorMessage('Please provide a test email address.');
      return;
    }
    
    setIsTesting(true);
    setTestResult(null);
    setTestErrorMessage(null);
    try {

      if (formData.provider === 'Microsoft 365') {
        const res = await fetch('/api/send-test-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, testEmail })
        });
        
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${res.status}`);
        }
        
        setTestResult('success');
      } else {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setTestResult('success');
      }
    } catch (e: any) {
      console.error(e);
      setTestResult('error');
      setTestErrorMessage(e.message || 'Unknown error occurred.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveResult(null);
    setTestErrorMessage(null);
    try {
      await saveEmailSettings(formData);
      setSaveResult('success');
      setTimeout(() => setSaveResult(null), 3000);
    } catch (e: any) {
      console.error(e);
      setSaveResult('error');
      setTestErrorMessage(e.message || 'Unknown error');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500">
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Email & Routing</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Configure custom SMTP for system emails.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/10 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-7xl space-y-6">
          
          <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
            {[
              { id: 'smtp', label: 'OAuth2 / SMTP', icon: Mail },
              { id: 'ticket-portal', label: 'Ticket Portal & Redirection', icon: Globe },
              { id: 'email-to-ticket', label: 'Email-to-Ticket Automation', icon: Inbox },
              { id: 'mailboxes', label: 'Sender Identities', icon: Mail },
              { id: 'templates', label: 'Email Templates', icon: LayoutTemplate },
              { id: 'branding', label: 'Company Branding', icon: Palette }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                  activeTab === tab.id ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'smtp' && (
            <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <h3 className="font-display text-lg font-medium">OAuth2 Configuration</h3>
                <p className="text-sm text-muted-foreground mt-1">Configure outbound email delivery using modern OAuth2 authentication.</p>
              </div>
            </div>



            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2 space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
                <h4 className="font-medium text-sm border-b border-border pb-2 mb-4">OAuth2 Credentials</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Provider</label>
                    <select 
                      value={formData.provider || 'Microsoft 365'}
                      onChange={e => setFormData({...formData, provider: e.target.value})}
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                    >
                      <option value="Microsoft 365">Microsoft 365</option>
                      <option value="Google Workspace">Google Workspace</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Client ID</label>
                    <input 
                      type="text" 
                      value={formData.client_id || ''}
                      onChange={e => setFormData({...formData, client_id: e.target.value})}
                      placeholder="e.g. a1b2c3d4-e5f6-7890..." 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Tenant ID</label>
                    <input 
                      type="text" 
                      value={formData.tenant_id || ''}
                      onChange={e => setFormData({...formData, tenant_id: e.target.value})}
                      placeholder="e.g. 1234abcd-56ef-7890..." 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">From Name</label>
                    <input 
                      type="text" 
                      value={formData.from_name || ''}
                      onChange={e => setFormData({...formData, from_name: e.target.value})}
                      placeholder="e.g. IT Helpdesk" 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">From Email</label>
                    <input 
                      type="email" 
                      value={formData.from_email || ''}
                      onChange={e => setFormData({...formData, from_email: e.target.value})}
                      placeholder="e.g. support@company.com" 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Client Secret</label>
                    <input 
                      type="password"
                      value={formData.client_secret || ''}
                      onChange={e => setFormData({...formData, client_secret: e.target.value})}
                      placeholder="••••••••••••••••" 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                    />
                    <p className="mt-1 text-[11px] text-amber-600/80 font-medium">Important: Provide the Client Secret <strong>Value</strong>, not the Secret ID.</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Test Email Address (To)</label>
                    <input 
                      type="email"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="e.g. your.name@company.com" 
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                    />
                  </div>
                  <div className="col-span-2 pt-4 flex flex-col gap-2 border-t border-border mt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Status</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{formData.is_active ? 'Active' : 'Disabled'}</span>
                        <button 
                          onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${formData.is_active ? 'bg-primary' : 'bg-muted'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>
                    {formData.is_active && emailSettings?.id && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                        </span>
                        <span className="text-xs font-medium text-emerald-600">Connected to {formData.provider || 'Microsoft 365'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-4 pt-6 pb-24 border-t border-border">
              {saveResult === 'success' && <span className="text-sm font-medium text-emerald-600 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Settings Saved</span>}
              {saveResult === 'error' && <span className="text-sm font-medium text-red-600 flex items-center gap-2" title={testErrorMessage || ''}><X className="h-4 w-4" /> {testErrorMessage || 'Failed to Save'}</span>}
              {testResult === 'success' && <span className="text-sm font-medium text-emerald-600 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Email Sent Successfully</span>}
              {testResult === 'error' && <span className="text-sm font-medium text-red-600 flex items-center gap-2" title={testErrorMessage || ''}><X className="h-4 w-4" /> {testErrorMessage || 'Email Failed to Send'}</span>}
              <button 
                onClick={handleTestConnection}
                disabled={isTesting || !testEmail}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-600 text-emerald-600 bg-transparent px-6 py-2.5 text-sm font-medium transition hover:bg-emerald-600/10 disabled:opacity-50"
              >
                {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Test Connection
              </button>
              <button onClick={handleSaveSettings} disabled={isSaving} className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50 shadow-lg shadow-primary/20">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
              </button>
            </div>
          </div>
          )}

          {activeTab === 'mailboxes' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h3 className="font-display text-lg font-medium">Sender Identities</h3>
                  <p className="text-sm text-muted-foreground mt-1">Configure available "From" addresses for agents to use when replying to tickets.</p>
                </div>
                <button 
                  onClick={() => handleEditMailbox()}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 shadow-sm"
                >
                  <Plus className="h-4 w-4" /> Add Mailbox
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {mailboxes.map((mailbox) => (
                  <div key={mailbox.id} className="relative rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-foreground">{mailbox.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">{mailbox.email}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditMailbox(mailbox)} className="p-1.5 text-muted-foreground hover:text-primary transition rounded hover:bg-primary/10">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => deleteMailbox(mailbox.id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition rounded hover:bg-red-500/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Department:</span>
                      <span className="font-medium bg-muted px-2 py-0.5 rounded text-foreground">{mailbox.department}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h3 className="font-display text-lg font-medium">Predefined Email Templates</h3>
                  <p className="text-sm text-muted-foreground mt-1">Configure automated email templates for ITSM workflows and ticket lifecycle events. Predefined templates cannot be deleted.</p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3 font-medium">Template</th>
                      <th className="px-6 py-3 font-medium">Event</th>
                      <th className="px-6 py-3 font-medium">Status</th>
                      <th className="px-6 py-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {emailTemplates.map((template) => (
                      <tr key={template.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-foreground">{template.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{template.category || 'System Default'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-xs bg-muted px-2 py-1 rounded border border-border font-mono">{template.event_name || 'UNKNOWN'}</code>
                        </td>
                        <td className="px-6 py-4">
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border",
                            template.is_active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-muted text-muted-foreground border-border"
                          )}>
                            {template.is_active ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => handleEditTemplate(template)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors bg-primary/10 px-3 py-1.5 rounded-lg"
                          >
                            <Edit className="h-3.5 w-3.5" /> Configure
                          </button>
                        </td>
                      </tr>
                    ))}
                    {emailTemplates.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                          <LayoutTemplate className="h-8 w-8 mx-auto mb-3 opacity-20" />
                          <p>No predefined templates found in the database.</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h3 className="font-display text-lg font-medium">Company Branding</h3>
                  <p className="text-sm text-muted-foreground mt-1">Configure logos and global header/footer HTML injected into all outbound emails.</p>
                </div>
              </div>

              <div className="space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Company Logo URL</label>
                  <input 
                    type="url" 
                    value={formData.company_logo_url || ''}
                    onChange={e => setFormData({...formData, company_logo_url: e.target.value})}
                    placeholder="https://..." 
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  />
                  {formData.company_logo_url && (
                    <div className="mt-3 p-3 border border-border rounded-lg bg-muted/20 inline-block">
                      <img src={formData.company_logo_url} alt="Logo Preview" className="h-10 object-contain" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Global Header HTML</label>
                  <textarea 
                    value={formData.header_html || ''}
                    onChange={e => setFormData({...formData, header_html: e.target.value})}
                    placeholder="<div style='text-align: center;'>...</div>" 
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono h-24 resize-y" 
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Global Footer HTML</label>
                  <textarea 
                    value={formData.footer_html || ''}
                    onChange={e => setFormData({...formData, footer_html: e.target.value})}
                    placeholder="<div style='font-size: 12px; color: #888;'>...</div>" 
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono h-24 resize-y" 
                  />
                </div>
                
                <div className="pt-4 border-t border-border flex justify-end">
                  <button onClick={handleSaveSettings} disabled={isSaving} className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50">
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Branding
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ticket-portal' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h3 className="font-display text-lg font-medium">Ticket Portal & Redirection Configuration</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure the destination URL used in ticket notification emails when users and technicians click &ldquo;View Ticket&rdquo;.
                  </p>
                </div>
              </div>

              {portalSaveNotice && (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-xs font-medium text-emerald-600 dark:text-emerald-400 animate-in fade-in">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span>{portalSaveNotice}</span>
                </div>
              )}

              {portalError && (
                <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-xs font-medium text-destructive animate-in fade-in">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{portalError}</span>
                </div>
              )}

              <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-6">
                <div>
                  <h4 className="font-medium text-sm border-b border-border pb-2 mb-4">Destination Portal Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                        Ticket Tool URL (Base URL) <span className="text-destructive">*</span>
                      </label>
                      <div className="relative">
                        <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="url"
                          value={portalUrl}
                          onChange={(e) => setPortalUrl(e.target.value)}
                          placeholder="https://sdtest.pinochle.ai"
                          className="w-full rounded-lg border border-input bg-background pl-9 pr-3 py-2 text-sm outline-none transition focus:border-primary font-mono"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Default: <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">https://sdtest.pinochle.ai</code>. Must begin with <code className="font-mono">https://</code>.
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                        Ticket Detail Route Template
                      </label>
                      <input
                        type="text"
                        value={detailPath}
                        onChange={(e) => setDetailPath(e.target.value)}
                        placeholder="/tickets/{ticket_number}"
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono"
                      />
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Tokens <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">&#123;ticket_number&#125;</code> or <code className="bg-muted px-1.5 py-0.5 rounded text-[11px] font-mono">&#123;ticket_id&#125;</code> are replaced dynamically.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Live URL Preview */}
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5" /> Live URL Preview
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Sample Ticket: INC-9389
                    </span>
                  </div>
                  <div className="rounded-lg border border-border bg-background px-3.5 py-2.5 font-mono text-xs text-foreground select-all break-all shadow-inner">
                    {portalUrl.replace(/\/+$/, '')}{detailPath.replace('{ticket_number}', 'INC-9389').replace('{ticket_id}', 'INC-9389')}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    This exact link will be embedded into automated emails sent to requesters and tech support technicians.
                  </p>
                </div>

                {/* Interactive Email Notification Sample Preview */}
                <div className="space-y-2.5 pt-2 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-primary" /> Email Notification Preview (Section 23)
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Ticket Created Acknowledgement
                    </span>
                  </div>

                  <div className="rounded-xl border border-border bg-background p-6 shadow-sm max-w-xl mx-auto space-y-4">
                    <div className="border-b-2 border-primary pb-2.5">
                      <h4 className="font-semibold text-foreground text-base">Support Request Acknowledgement</h4>
                    </div>
                    <p className="text-sm text-foreground">Dear <strong>Mukeshkumar Krishnan</strong>,</p>
                    <p className="text-sm text-muted-foreground">This is an acknowledgement mail for your request.</p>
                    <p className="text-sm text-muted-foreground">Your request has been created with ID <strong className="font-mono text-foreground">INC-9389</strong>.</p>
                    
                    <div className="bg-muted/40 border-l-4 border-primary p-3 rounded text-xs space-y-1">
                      <div className="text-muted-foreground uppercase text-[10px] font-semibold">The title of the request is:</div>
                      <div className="font-medium text-foreground">Request for Bluetooth USB Barcode Printer for Asset Management</div>
                      <div className="text-muted-foreground uppercase text-[10px] font-semibold pt-1">The current status of the request is:</div>
                      <div className="font-bold text-emerald-600">Open</div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      The status of the request can be tracked at:
                      <div className="font-mono text-primary text-[11px] underline mt-0.5 break-all">
                        {portalUrl.replace(/\/+$/, '')}{detailPath.replace('{ticket_number}', 'INC-9389').replace('{ticket_id}', 'INC-9389')}
                      </div>
                    </div>

                    <div className="pt-2">
                      <a
                        href={`${portalUrl.replace(/\/+$/, '')}${detailPath.replace('{ticket_number}', 'INC-9389').replace('{ticket_id}', 'INC-9389')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-primary-foreground shadow-sm hover:brightness-110 transition-all text-center"
                      >
                        View Ticket
                      </a>
                    </div>
                  </div>
                </div>

                {/* Footer Controls & Audit Info */}
                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="text-xs text-muted-foreground">
                    {portalAudit?.updatedAt ? (
                      <span>
                        Last updated on <strong>{new Date(portalAudit.updatedAt).toLocaleString()}</strong>
                        {portalAudit.updatedByName ? ` by ${portalAudit.updatedByName}` : ''}
                      </span>
                    ) : (
                      <span>Using system default settings (not yet modified).</span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    <button
                      type="button"
                      onClick={handleTestLink}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition hover:bg-muted text-foreground"
                    >
                      <ExternalLink className="h-4 w-4 text-primary" />
                      <span>Test Link</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleSavePortal}
                      disabled={isSavingPortal}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:brightness-110 transition-all disabled:opacity-50"
                    >
                      {isSavingPortal ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          <span>Save Configuration</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email-to-ticket' && (
            <div className="space-y-6">
              {/* Header Status Banner */}
              <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="flex h-3 w-3 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                    <h3 className="font-display text-lg font-medium">Email-to-Ticket Ingestion Engine</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Automatically converts incoming emails sent to <strong className="text-foreground">{formData.from_email || 'techsupport@pinochle.ai'}</strong> into tracked ITSM tickets with sequential IDs, attachments, and reply threading.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border",
                    e2tEnabled ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-muted text-muted-foreground border-border"
                  )}>
                    {e2tEnabled ? 'Automation Active' : 'Automation Paused'}
                  </span>
                  <button
                    onClick={() => setE2tEnabled(!e2tEnabled)}
                    className="neo-interactive rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium hover:bg-muted"
                  >
                    {e2tEnabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>

              {/* Grid: Ingestion Settings & Mailbox Sync */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Card 1: Configuration & Business Rules */}
                <div className="space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-primary" />
                      Ingestion & Routing Rules
                    </h4>
                    {e2tSaveNotice && (
                      <span className="text-xs font-medium text-emerald-500 flex items-center gap-1">
                        <Check className="h-3 w-3" /> Saved
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Support Ingestion Mailbox</label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="text" 
                        readOnly 
                        value={formData.from_email || 'techsupport@pinochle.ai'} 
                        className="w-full rounded-lg border border-input bg-muted/30 px-3 py-2 text-sm font-mono text-foreground cursor-not-allowed" 
                      />
                      <span className="text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-md font-medium border border-primary/20 whitespace-nowrap">Primary</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">Configured in OAuth2 / SMTP tab. Users send tickets to this address.</p>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Unknown Sender Policy</label>
                    <div className="space-y-2 mt-1.5">
                      <label className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition",
                        unknownSenderPolicy === 'create' ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                      )}>
                        <input 
                          type="radio" 
                          name="unknownPolicy" 
                          checked={unknownSenderPolicy === 'create'} 
                          onChange={() => setUnknownSenderPolicy('create')} 
                          className="mt-0.5"
                        />
                        <div>
                          <div className="text-xs font-medium text-foreground">Option A: Create External Ticket (Recommended)</div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">Automatically provisions requester profile so the ticket is tracked and visible to agents.</p>
                        </div>
                      </label>
                      <label className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition",
                        unknownSenderPolicy === 'reject' ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                      )}>
                        <input 
                          type="radio" 
                          name="unknownPolicy" 
                          checked={unknownSenderPolicy === 'reject'} 
                          onChange={() => setUnknownSenderPolicy('reject')} 
                          className="mt-0.5"
                        />
                        <div>
                          <div className="text-xs font-medium text-foreground">Option B: Reject & Send Registration Notice</div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">Declines the ticket and bounces a message requesting email from a registered account.</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Default Priority</label>
                      <select 
                        value={defaultPriority}
                        onChange={e => setDefaultPriority(e.target.value)}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                      >
                        <option value="Low">Low (P4)</option>
                        <option value="Medium">Medium (P3)</option>
                        <option value="High">High (P2)</option>
                        <option value="Urgent">Urgent (P1)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Default Category</label>
                      <select 
                        value={defaultCategory}
                        onChange={e => setDefaultCategory(e.target.value)}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                      >
                        <option value="Hardware">Hardware</option>
                        <option value="Network">Network</option>
                        <option value="Account Access">Account Access</option>
                        <option value="Email Support">Email Support</option>
                        <option value="General">General</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-border">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={autoAssign} 
                        onChange={e => setAutoAssign(e.target.checked)} 
                        className="rounded border-input text-primary focus:ring-primary" 
                      />
                      <span className="text-xs text-foreground font-medium">Auto-assign to IT / Service Desk team</span>
                    </label>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span>Loop & Duplicate Protection: Message-ID deduplication & autoreply filter enabled.</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border flex justify-end">
                    <button 
                      onClick={handleSaveE2TSettings}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 transition"
                    >
                      <Save className="h-3.5 w-3.5" /> Save Ingestion Policy
                    </button>
                  </div>
                </div>

                {/* Card 2: Mailbox Sync Engine & Diagnostics */}
                <div className="space-y-4 rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-border pb-3">
                      <h4 className="font-medium text-sm flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-primary" />
                        Dual Ingestion Architecture
                      </h4>
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground">Graph + Webhook</span>
                    </div>

                    <div className="space-y-3">
                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                            Inbound Webhook API
                          </span>
                          <span className="text-[11px] font-mono text-emerald-500 font-semibold">ONLINE</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Endpoint: <code className="bg-background px-1.5 py-0.5 rounded border border-border text-foreground">POST /api/email/incoming</code>. Compatible with Microsoft Graph webhook subscriptions, SendGrid inbound parse, AWS SES, and mail gateways.
                        </p>
                      </div>

                      <div className="p-3.5 rounded-lg border border-border bg-muted/20 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full bg-sky-500"></span>
                            Microsoft Graph Mailbox Polling
                          </span>
                          <span className="text-[11px] font-mono text-sky-500 font-semibold">READY</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Synchronizes unread messages from <code className="text-foreground">{formData.from_email || 'techsupport@pinochle.ai'}</code> via Microsoft Graph API client credentials.
                        </p>
                        <div className="pt-2">
                          <button
                            onClick={handleSyncMailbox}
                            disabled={isSyncingMailbox}
                            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-1.5 text-xs font-medium hover:bg-muted transition disabled:opacity-50"
                          >
                            {isSyncingMailbox ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            Sync Mailbox Now
                          </button>
                        </div>
                      </div>

                      {syncResult && (
                        <div className={cn(
                          "p-3 rounded-lg border text-xs leading-relaxed",
                          syncResult.success ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-amber-500/30 bg-amber-500/10 text-amber-300"
                        )}>
                          <div className="font-semibold flex items-center gap-1.5">
                            {syncResult.success ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                            {syncResult.status || (syncResult.success ? 'Sync Completed' : 'Diagnostics Notice')}
                          </div>
                          <p className="mt-1 text-[11px]">
                            {syncResult.error || syncResult.message || `Processed ${syncResult.syncedCount || 0} messages.`}
                          </p>
                          {syncResult.note && (
                            <p className="mt-1 text-[10px] text-muted-foreground">{syncResult.note}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border text-[11px] text-muted-foreground flex items-center justify-between">
                    <span>Reply Threading: Enabled ([TKT-YYYY-XXXXXX])</span>
                    <span>Attachments: Supabase Storage</span>
                  </div>
                </div>

              </div>

              {/* Card 3: Interactive Email Ingestion Simulator */}
              <div className="space-y-4 rounded-xl border border-primary/20 bg-card p-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-border pb-3">
                  <div>
                    <h4 className="font-medium text-sm flex items-center gap-2 text-foreground">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Interactive Email Ingestion Simulator
                    </h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Simulate an incoming user email or reply in real-time to verify ticket generation, organization matching, and comment threading.
                    </p>
                  </div>
                  <span className="text-[11px] bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-full font-medium">
                    Live Testing Tool
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Sender Email (From)</label>
                    <input 
                      type="email"
                      value={simSenderEmail}
                      onChange={e => setSimSenderEmail(e.target.value)}
                      placeholder="e.g. rvasudevan@pinochle.ai"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Sender Display Name</label>
                    <input 
                      type="text"
                      value={simSenderName}
                      onChange={e => setSimSenderName(e.target.value)}
                      placeholder="e.g. Raja Vasudevan"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block flex items-center justify-between">
                    <span>Subject Line</span>
                    <span className="text-[11px] text-muted-foreground font-normal">Include <code className="text-primary">[TKT-YYYY-XXXXXX]</code> to simulate a reply</span>
                  </label>
                  <input 
                    type="text"
                    value={simSubject}
                    onChange={e => setSimSubject(e.target.value)}
                    placeholder="e.g. Laptop is not working OR Re: [TKT-2026-000008] Update"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Email Message Body</label>
                  <textarea 
                    value={simBody}
                    onChange={e => setSimBody(e.target.value)}
                    rows={4}
                    placeholder="Enter email content..."
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-sans resize-y"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSimSenderEmail('rvasudevan@pinochle.ai');
                        setSimSenderName('Raja Vasudevan');
                        setSimSubject('Laptop is not working');
                        setSimBody('Hi IT Team,\n\nMy laptop is not starting after restarting.\nPlease help me resolve this issue.\n\nThanks,\nRaja');
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Sample 1: New Ticket
                    </button>
                    <span className="text-muted-foreground">&bull;</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSimSenderEmail('rvasudevan@pinochle.ai');
                        setSimSenderName('Raja Vasudevan');
                        setSimSubject('Re: [TKT-2026-000008] Ticket Created — Laptop is not working');
                        setSimBody('I have also tried connecting the charger, but the laptop still does not start.');
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Sample 2: Reply to Ticket
                    </button>
                  </div>

                  <button
                    onClick={handleSimulateEmail}
                    disabled={isSimulating || !simSenderEmail || !simSubject}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition disabled:opacity-50 shadow-sm"
                  >
                    {isSimulating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Simulate Inbound Email
                  </button>
                </div>

                {/* Simulator Live Result Display */}
                {simResult && (
                  <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        Simulation Result: {simResult.action === 'ticket_created' ? 'Ticket Created' : simResult.action === 'comment_added' ? 'Reply Appended as Comment' : simResult.status}
                      </span>
                      <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                        Status: {simResult.status}
                      </span>
                    </div>

                    {simResult.ticket && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-card p-3 rounded-lg border border-border text-xs">
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase">Ticket ID</span>
                          <strong className="font-mono text-primary text-sm">{simResult.ticket.number}</strong>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase">Source</span>
                          <strong className="text-emerald-500 font-semibold">{simResult.ticket.source || 'Email'}</strong>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase">Priority / Status</span>
                          <span>{simResult.ticket.priority} &bull; {simResult.ticket.status}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase">Requester</span>
                          <span className="truncate block">{simResult.ticket.requester_email || simSenderEmail}</span>
                        </div>
                      </div>
                    )}

                    {simResult.comment && (
                      <div className="bg-card p-3 rounded-lg border border-border text-xs space-y-1">
                        <span className="text-muted-foreground block text-[10px] uppercase">Appended Comment Content</span>
                        <p className="text-foreground italic">"{simResult.comment.content}"</p>
                      </div>
                    )}

                    {simResult.error && (
                      <div className="text-xs text-red-400 bg-red-500/10 p-2.5 rounded border border-red-500/20">
                        {simResult.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {isTemplateModalOpen && editingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6 border-b border-border pb-4">
              <div>
                <h2 className="font-display text-xl font-semibold">{editingTemplate.id ? 'Edit Template' : 'Create Template'}</h2>
                <p className="text-xs text-muted-foreground mt-1">Configure rich text HTML email template</p>
              </div>
              <button onClick={() => setIsTemplateModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Template Name</label>
                  <input 
                    value={editingTemplate.name || ''} 
                    onChange={e => setEditingTemplate({...editingTemplate, name: e.target.value})}
                    placeholder="e.g., Ticket Created"
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Language</label>
                  <select 
                    value={editingTemplate.language || 'en'} 
                    onChange={e => setEditingTemplate({...editingTemplate, language: e.target.value})}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                  >
                    <option value="en">English (en)</option>
                    <option value="es">Spanish (es)</option>
                    <option value="fr">French (fr)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1.5 flex justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Email Subject</span>
                  <span className="text-primary font-normal normal-case">Supports Placeholders</span>
                </label>
                <input 
                  value={editingTemplate.subject_template || ''} 
                  onChange={e => setEditingTemplate({...editingTemplate, subject_template: e.target.value})}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                />
              </div>

              <div>
                <label className="mb-1.5 flex justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <span>Email Body (HTML)</span>
                  <span className="text-primary font-normal normal-case">Variables: {'{{TicketNumber}}'}, {'{{RequesterName}}'}, {'{{TechnicianName}}'}, {'{{PortalLink}}'}, etc.</span>
                </label>
                <div className="bg-background rounded-lg border border-input overflow-hidden">
                  <ReactQuill 
                    theme="snow" 
                    value={editingTemplate.html_body || ''} 
                    onChange={(content) => setEditingTemplate({...editingTemplate, html_body: content})} 
                    className="h-64 mb-10"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-border">
              <button 
                onClick={() => setIsTemplateModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveTemplate}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                <Save className="h-4 w-4" /> Save Template
              </button>
            </div>
          </div>
        </div>
      )}
      {isMailboxModalOpen && editingMailbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6 border-b border-border pb-4">
              <div>
                <h2 className="font-display text-xl font-semibold">{editingMailbox.id ? 'Edit Mailbox' : 'Add Mailbox'}</h2>
                <p className="text-xs text-muted-foreground mt-1">Configure sender identity</p>
              </div>
              <button onClick={() => setIsMailboxModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email Address</label>
                <input 
                  type="email"
                  value={editingMailbox.email} 
                  onChange={e => setEditingMailbox({...editingMailbox, email: e.target.value})}
                  placeholder="e.g. support@company.com"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary font-mono" 
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display Name</label>
                <input 
                  type="text"
                  value={editingMailbox.name} 
                  onChange={e => setEditingMailbox({...editingMailbox, name: e.target.value})}
                  placeholder="e.g. IT Support"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Department (Optional)</label>
                <input 
                  type="text"
                  value={editingMailbox.department} 
                  onChange={e => setEditingMailbox({...editingMailbox, department: e.target.value})}
                  placeholder="e.g. IT, HR, Finance"
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-border">
              <button 
                onClick={() => setIsMailboxModalOpen(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveMailbox}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
              >
                <Save className="h-4 w-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
